import { useState } from "react";
import { Activity, DatabaseBackup, AlertTriangle, Play, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { EmptyState, PageHeader, Panel, RowsSkeleton, Tag } from "@/components/app/kit";
import { useFetch } from "@/hooks/useServerList";
import { api, formatApiError } from "@/lib/api";
import { fmtDateTime } from "@/lib/i18n";

const JOB_LABELS = { subscription_lifecycle: "Cycle des abonnements", tenant_notifications: "Notifications des cabinets", backups: "Sauvegardes automatiques", retention: "Purge selon les durées de conservation" };

const STATUS = { success: "réussie", error: "en erreur", running: "en cours" };

const Stat = ({ label, value, tone = "slate", testid }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={testid}>
    <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
    <p className={`mt-1 text-2xl font-semibold text-${tone}-700`}>{value}</p>
  </div>
);

const Jobs = ({ jobs, onRun, busy }) => (
  <Panel title="Tâches automatisées">
    {Object.entries(jobs).map(([name, j]) => (
      <div key={name} className="flex flex-wrap items-center gap-3 py-2 border-b border-slate-100 last:border-0 text-sm" data-testid={`job-${name}`}>
        <span className="font-medium">{JOB_LABELS[name] || name}</span>
        <Tag tone={j.last_status === "success" ? "emerald" : j.last_status === "error" ? "red" : "slate"}>{STATUS[j.last_status] || "jamais exécutée"}</Tag>
        <span className="text-xs text-slate-500">Dernier succès : {j.last_success ? fmtDateTime(j.last_success) : "—"} · erreurs 24 h : {j.errors_24h}</span>
        <Button size="sm" variant="outline" className="ml-auto" disabled={busy} onClick={() => onRun(name)} data-testid={`job-run-${name}`}><Play className="h-3.5 w-3.5 mr-1" />Lancer</Button>
      </div>
    ))}
  </Panel>
);

const Errors = ({ rows }) => (
  <Panel title="Dernières erreurs techniques">
    {!rows.length ? <EmptyState icon={Activity} title="Aucune erreur enregistrée" /> : rows.map((e) => (
      <div key={e.id} className="py-2 border-b border-slate-100 last:border-0 text-xs font-mono break-all" data-testid="error-row">
        <span className="text-slate-500">{fmtDateTime(e.at)} · {e.request_id}</span> <span className="text-slate-700">{e.method} {e.path}</span> <span className="text-red-700">{e.error}</span>
      </div>))}
  </Panel>
);

export default function Monitoring() {
  const { data, reload, error } = useFetch("/platform/monitoring");
  const [busy, setBusy] = useState(false);
  const run = async (job) => {
    setBusy(true);
    try { await api.post("/platform/automations/run", { job }); toast.success("Tâche lancée"); setTimeout(reload, 3000); } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <div>
      <PageHeader eyebrow="Exploitation" title="Supervision technique" subtitle="État de la base, des tâches automatisées, des sauvegardes et des erreurs serveur."
        actions={<Button variant="outline" onClick={reload} data-testid="monitoring-refresh"><RefreshCw className="h-4 w-4 mr-2" />Actualiser</Button>} />
      {error ? <EmptyState icon={AlertTriangle} title="Réservé aux super administrateurs" text={error} /> : !data ? <RowsSkeleton /> : (
        <div className="space-y-6">
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <Stat label="Base de données" value={data.database === "ok" ? "OK" : "KO"} tone="emerald" testid="stat-db" />
            <Stat label="Erreurs 24 h" value={data.errors_24h} tone={data.errors_24h ? "red" : "slate"} testid="stat-errors" />
            <Stat label="Connexions échouées 24 h" value={data.failed_logins_24h} testid="stat-failed-logins" />
            <Stat label="Sauvegardes" value={data.backups.total} testid="stat-backups" />
            <Stat label="E-mails non envoyés" value={data.outbox_unsent} tone="amber" testid="stat-outbox" />
          </div>
          <Jobs jobs={data.jobs} onRun={run} busy={busy} />
          <Panel title="Sauvegardes">
            <p className="text-sm text-slate-600 mb-3" data-testid="last-restore-test"><DatabaseBackup className="inline h-4 w-4 mr-1" />Dernier test de restauration réussi : {data.backups.last_successful_restore_test ? fmtDateTime(data.backups.last_successful_restore_test) : "aucun"}</p>
            {data.backups.tenants_without_recent_backup.length ? data.backups.tenants_without_recent_backup.map((t) => (
              <p key={t.tenant_id} className="text-sm text-amber-700" data-testid="stale-backup">{t.name} — dernière sauvegarde : {t.last_backup ? fmtDateTime(t.last_backup) : "aucune"}</p>
            )) : <p className="text-sm text-emerald-700">Tous les cabinets ont une sauvegarde de moins de 48 h.</p>}
          </Panel>
          <Errors rows={data.recent_errors} />
        </div>
      )}
    </div>
  );
}
